
import React, { useEffect } from 'react';
import { Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom';
import { initNotifications } from './services/notificationService';
import BookingConfirmation from './app/protected/Guest/Dashboard/BookingConfirmation';
import GuestDashboard from './app/protected/Guest/Dashboard/GuestDashboard';
import PropertyDetails from './app/protected/Guest/Dashboard/PropertyDetails/PropertyDetails';
import HomePage from './app/protected/Host/HomePage/HomePage';
import CreatePropertyPage from './app/protected/Host/HomePage/MyProperty/CreateProperty/CreateProperty';
import LandingPage from './app/protected/LandingPage/LandingPage';
import Login from './app/protected/Login/Login';
import SignUp from './app/protected/SignUp/SignUp';
import ForgotPassword from './app/protected/Login/ForgotPassword';
import ResetPassword from './app/protected/Login/ResetPassword';
import About from './app/protected/LandingPage/About';
import Contact from './app/protected/LandingPage/Contact';
import Privacy from './app/protected/LandingPage/Privacy';
import Terms from './app/protected/LandingPage/Terms';
import FAQ from './app/protected/LandingPage/FAQ';
import RefundPolicy from './app/protected/LandingPage/RefundPolicy';
import Blog from './app/protected/LandingPage/Blog';
import CityLanding from './pages/CityLanding';
import PropertySeo from './pages/PropertySeo';
import SearchSeo from './pages/SearchSeo';
import Careers from './app/protected/LandingPage/Careers';
import ProtectedRoute from './app/protectedRoute/ProtectedRoute';
import PaymentFailure from './app/protected/Guest/PaymentStatus/PaymentFailure';
import PaymentSuccess from './app/protected/Guest/PaymentStatus/PaymentSuccess';
import { UserProvider } from './app/protectedRoute/UserContext';
import { NotificationProvider } from './context/NotificationContext';
import AdminDashboard from './app/protected/Admin/Dashboard/AdminDashboard';
import GuestSearchResults from './app/protected/Guest/GuestSearchResults/GuestSearchResults';
import Wallet from './components/Wallet/Wallet';
import HostRoutes from './routes/HostRoutes';
import PropertyDetailsPage from './app/protected/Host/HomePage/PropertyDetailsPage';
import LandingFooter from './components/landing/Footer';
import NewPaymentSuccess from './pages/PaymentSuccess';
import NewPaymentFailure from './pages/PaymentFailure';
import PublicTicketForm from './pages/PublicTicketForm';
import PublicRatingForm from './pages/PublicRatingForm';
import GuestReviewForm from './pages/GuestReviewForm';
import SubscriptionPage from './components/SubscriptionPage/SubscriptionPage';
import Seo from './components/Seo/Seo';
import { organizationSchema, webSiteSchema, lodgingBusinessSchema, landingFaqSchema } from './components/Seo/schemas';

const AppContent: React.FC = () => {
  const location = useLocation();
  const isPropertyDetailPage = location.pathname.startsWith('/property/');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (localStorage.getItem('setting_push_notifications') !== 'false') {
      initNotifications();
    }
  }, []);
  const pagesWithOwnFooter = ['/', '/about', '/contact', '/privacy', '/terms', '/faq', '/refund-policy', '/blog', '/careers', '/login', '/signup', '/guest-search-results'];
  const showCommonFooter = !pagesWithOwnFooter.includes(location.pathname) && !isPropertyDetailPage;

  return (
    <>
      <Routes>
        {/* Landing - main indexable page with full JSON-LD */}
        <Route path="/" element={<><Seo path="/" jsonLd={[organizationSchema, webSiteSchema, lodgingBusinessSchema, landingFaqSchema]} /><LandingPage /></>} />

        {/* Public booking pages - indexable */}
        <Route path="/guest-search-results" element={<><SearchSeo /><GuestSearchResults /></>} />
        <Route path="/property/:property_id" element={<><PropertySeo /><PropertyDetails /></>} />

        {/* Public info pages - indexable */}
        <Route path="/pgs-in/:slug" element={<CityLanding />} />
        <Route path="/about" element={<><Seo title="About Us" description="Learn about Social Bunkr - India's budget short-stay booking platform with all-inclusive PG beds and 3 meals a day." path="/about" /><About /></>} />
        <Route path="/contact" element={<><Seo title="Contact Us" description="Get in touch with the Social Bunkr team. Email us at support@socialbunkr.in." path="/contact" /><Contact /></>} />
        <Route path="/faq" element={<><Seo title="FAQs" description="Frequently asked questions about booking on Social Bunkr - pricing, meals, check-in, refunds and more." path="/faq" /><FAQ /></>} />
        <Route path="/blog" element={<><Seo title="Blog" description="Travel tips, budget stay guides, and city guides for backpackers and budget travellers in India." path="/blog" /><Blog /></>} />
        <Route path="/careers" element={<><Seo title="Careers" description="Join the Social Bunkr team. We're building India's most affordable short-stay booking platform." path="/careers" /><Careers /></>} />
        <Route path="/privacy" element={<><Seo title="Privacy Policy" path="/privacy" /><Privacy /></>} />
        <Route path="/terms" element={<><Seo title="Terms of Service" path="/terms" /><Terms /></>} />
        <Route path="/refund-policy" element={<><Seo title="Refund Policy" path="/refund-policy" /><RefundPolicy /></>} />

        {/* Auth - noindex */}
        <Route path="/login" element={<><Seo title="Log In" path="/login" noindex /><Login /></>} />
        <Route path="/signup" element={<><Seo title="Sign Up" path="/signup" noindex /><SignUp /></>} />
        <Route path="/forgot-password" element={<><Seo title="Forgot Password" path="/forgot-password" noindex /><ForgotPassword /></>} />
        <Route path="/reset-password" element={<><Seo title="Reset Password" path="/reset-password" noindex /><ResetPassword /></>} />

        {/* Payment & booking status - noindex */}
        <Route path="/booking-confirmation/:booking_id" element={<><Seo title="Booking Confirmed" noindex /><BookingConfirmation /></>} />
        <Route path="/payment-success/:booking_id" element={<><Seo title="Payment Successful" noindex /><PaymentSuccess /></>} />
        <Route path="/payment-failure/:booking_id" element={<><Seo title="Payment Failed" noindex /><PaymentFailure /></>} />
        <Route path="/new-payment-success/:booking_id" element={<><Seo title="Payment Successful" noindex /><NewPaymentSuccess /></>} />
        <Route path="/new-payment-failure/:booking_id" element={<><Seo title="Payment Failed" noindex /><NewPaymentFailure /></>} />

        {/* Tenant forms - noindex */}
        <Route path="/submit/ticket" element={<><Seo title="Raise a Ticket" noindex /><PublicTicketForm /></>} />
        <Route path="/submit/rating" element={<><Seo title="Rate Your Stay" noindex /><PublicRatingForm /></>} />
        <Route path="/submit/guest-review" element={<><Seo title="Rate Your Stay" noindex /><GuestReviewForm /></>} />

        {/* Protected guest routes - noindex */}
        <Route element={<ProtectedRoute roles={['guest']} />}>
          <Route path="/dashboard" element={<><Seo title="My Dashboard" noindex /><GuestDashboard /></>} />
          <Route path="/wallet" element={<><Seo title="Wallet" noindex /><Wallet /></>} />
        </Route>

        {/* Protected host routes - noindex */}
        <Route element={<ProtectedRoute roles={['host']} />}>
          <Route path="/home" element={<><Seo title="Host Dashboard" noindex /><HomePage /></>} />
          <Route path="/subscription" element={<><Seo title="Subscription" noindex /><SubscriptionPage /></>} />
          <Route path="/create-property" element={<><Seo title="Create Property" noindex /><CreatePropertyPage /></>} />
          <Route path="/host/property/:id" element={<PropertyDetailsPage />} />
          <Route path="/host/*" element={<HostRoutes />} />
        </Route>

        {/* Admin - noindex */}
        <Route element={<ProtectedRoute roles={['admin']} />}>
          <Route path="/admin/dashboard" element={<><Seo title="Admin Dashboard" noindex /><AdminDashboard /></>} />
        </Route>
      </Routes>
      {isPropertyDetailPage && <LandingFooter />}
    </>
  );
};

const App: React.FC = () => {
  console.log('REACT_APP_USER_SERVICE_API_ENDPOINT:', process.env.REACT_APP_USER_SERVICE_API_ENDPOINT);

  return (
    <UserProvider>
      <NotificationProvider>
        <Router>
          <AppContent />
        </Router>
      </NotificationProvider>
    </UserProvider>
  );
};

export default App;
